import { isActiveMember } from './membership';
import { PERIOD_LABEL, WEEKDAY_LONG, formatDayMonth, periodOf, weekdayOf } from './dates';
import { compareNames } from './members';
import {
  type ExperienceIndex,
  type RoleRuleIssue,
  experienceIndex,
  isTrainee,
  roleRuleIssues,
  togetherPartnerIn,
} from './roleRules';
import type {
  AvailState,
  Blackout,
  DomainState,
  Member,
  Ministry,
  Period,
  Role,
  ScheduleEvent,
  ScheduleStatus,
} from './types';

/**
 * Regras da escala: o que cada evento precisa, quem está em cada função, o que é
 * conflito e qual o status derivado. Nada aqui é guardado — tudo é calculado a partir
 * do estado, para que status e conflito nunca fiquem desatualizados em relação às
 * pessoas escaladas.
 */

export const STATUS_ORDER: readonly ScheduleStatus[] = [
  'published',
  'complete',
  'partial',
  'conflict',
  'awaiting',
];

export const STATUS_LABEL: Record<ScheduleStatus, string> = {
  published: 'Publicada',
  complete: 'Escala completa',
  partial: 'Parcialmente preenchida',
  conflict: 'Conflito detectado',
  awaiting: 'Aguardando geração',
};

/** Mensagens padronizadas do handoff para as violações de regra. */
export const RULE_MESSAGES = {
  unavailable: 'Este participante não está disponível neste horário.',
  duplicate: 'Este participante já está escalado neste evento.',
  limit: 'Esta alteração excede o limite configurado para o período.',
  blackout: 'Este participante estará ausente nesta data.',
  sex: 'Esta função é restrita a outro sexo.',
} as const;

export interface Lookup {
  members: Map<string, Member>;
  ministries: Map<string, Ministry>;
  /** roleId → função e ministério a que pertence. */
  roles: Map<string, { role: Role; ministry: Ministry }>;
  /** Experiência de cada pessoa em cada função (das escalas do estado; vazio sem eventos). */
  experience: ExperienceIndex;
  /**
   * Hoje: nos eventos que já aconteceram, quem foi marcado (Escalas › Histórico) não gera
   * conflito de disponibilidade nem de ausência. Sem `today`, todo evento é conferido.
   */
  today: string | null;
}

export function buildLookup(
  state: Pick<DomainState, 'members' | 'ministries'> & { events?: readonly ScheduleEvent[] },
  today: string | null = null
): Lookup {
  const roles = new Map<string, { role: Role; ministry: Ministry }>();

  for (const ministry of state.ministries) {
    for (const role of ministry.roles) {
      roles.set(role.id, { role, ministry });
    }
  }

  let experience: ExperienceIndex | null = null;
  // Calculada só quando alguém pergunta (o seletor, a sugestão de substituto).
  const lazyExperience: ExperienceIndex = {
    count: (...args) => (experience ??= experienceIndex(state.events ?? [])).count(...args),
    add: (...args) => (experience ??= experienceIndex(state.events ?? [])).add(...args),
    remove: (...args) => (experience ??= experienceIndex(state.events ?? [])).remove(...args),
  };

  return {
    members: new Map(state.members.map((m) => [m.id, m])),
    ministries: new Map(state.ministries.map((m) => [m.id, m])),
    roles,
    experience: lazyExperience,
    today,
  };
}

/** As funções de todos os ministérios do evento (os pares restritos valem entre elas). */
export function eventRoles(event: ScheduleEvent, lookup: Lookup): Role[] {
  return event.ministryIds.flatMap((id) => lookup.ministries.get(id)?.roles ?? []);
}

export function availIndex(weekday: number, period: Period): number {
  return weekday * 3 + period;
}

export function availabilityAt(member: Member, date: string, time: string): AvailState {
  return member.availability[availIndex(weekdayOf(date), periodOf(time))] ?? 'unset';
}

export function blackoutOn(member: Member, date: string): Blackout | undefined {
  return member.blackouts.find((b) => date >= b.from && date <= b.to);
}

/** Quantidade da função no evento: a do evento quando ajustada, senão a padrão. */
export function quantityOf(event: ScheduleEvent, role: Role): number {
  return event.need[role.id] ?? role.qty;
}

export function isEnabledIn(member: Member, ministryId: string, roleId: string): boolean {
  return (member.roles[ministryId] ?? []).includes(roleId);
}

export function sexAllowed(role: Role, member: Member): boolean {
  return role.allowedSex === 'any' || role.allowedSex === member.sex;
}

export type IssueKind = 'blackout' | 'unavailable' | 'sex' | 'duplicate';

export interface Issue {
  kind: IssueKind;
  /** Rótulo curto ("Indisponível neste horário"). */
  label: string;
  /** Explicação com nome e período, para o alerta de conflito. */
  message: string;
}

function periodText(date: string, time: string): string {
  return `${WEEKDAY_LONG[weekdayOf(date)]!.toLowerCase()} · ${PERIOD_LABEL[periodOf(time)].toLowerCase()}`;
}

/**
 * Violação de regra obrigatória de uma pessoa numa função do evento. `past`: o evento já
 * aconteceu e a pessoa foi marcada como quem serviu — disponibilidade e ausência não valem.
 */
export function memberIssue(member: Member, role: Role, event: ScheduleEvent, past = false): Issue | null {
  const blackout = past ? undefined : blackoutOn(member, event.date);

  if (blackout) {
    return {
      kind: 'blackout',
      label: 'Ausência programada',
      message: `${member.name} estará ausente (${blackout.reason || 'ausência programada'}) de ${formatDayMonth(blackout.from)} a ${formatDayMonth(blackout.to)}.`,
    };
  }

  if (!past && availabilityAt(member, event.date, event.time) === 'unavailable') {
    return {
      kind: 'unavailable',
      label: 'Indisponível neste horário',
      message: `${member.name} não está disponível neste horário (${periodText(event.date, event.time)}).`,
    };
  }

  if (!sexAllowed(role, member)) {
    return {
      kind: 'sex',
      label: 'Restrição da função',
      message: `${role.name} é restrita a ${role.allowedSex === 'F' ? 'mulheres' : 'homens'}.`,
    };
  }

  return null;
}

export interface SlotPerson {
  member: Member;
  availability: AvailState;
  issue: Issue | null;
  /** Escalado além da quantidade da função. */
  extra: boolean;
}

export interface FunctionSlot {
  role: Role;
  /** Quantidade efetiva neste evento. */
  qty: number;
  people: SlotPerson[];
  open: number;
}

export interface MinistryGroup {
  ministry: Ministry;
  functions: FunctionSlot[];
}

export interface Conflict {
  member: Member;
  role: Role;
  ministry: Ministry;
  issue: Issue;
}

export interface PendingRole {
  role: Role;
  ministry: Ministry;
  have: number;
  missing: number;
}

export interface EventInfo {
  event: ScheduleEvent;
  groups: MinistryGroup[];
  filled: number;
  needed: number;
  conflicts: Conflict[];
  pending: PendingRole[];
  /** Ids de quem está escalado, sem repetição. */
  people: string[];
  status: ScheduleStatus;
}

export function statusOf(
  event: ScheduleEvent,
  counts: { conflicts: number; filled: number; needed: number }
): ScheduleStatus {
  if (!event.generated) return 'awaiting';
  if (counts.conflicts > 0) return 'conflict';
  if (counts.filled < counts.needed) return 'partial';

  return event.published ? 'published' : 'complete';
}

/**
 * Composição de um evento: por ministério e função, quem está escalado, o que falta e o
 * que viola regra. O progresso conta no máximo a quantidade de cada função — extras não
 * compensam vaga aberta em outra função.
 */
export function eventInfo(event: ScheduleEvent, lookup: Lookup): EventInfo {
  let filled = 0;
  let needed = 0;
  const conflicts: Conflict[] = [];
  const pending: PendingRole[] = [];
  const seen = new Set<string>();
  const past = lookup.today !== null && event.date < lookup.today;

  const groups: MinistryGroup[] = event.ministryIds.flatMap((ministryId) => {
    const ministry = lookup.ministries.get(ministryId);

    if (!ministry) return [];

    const functions = ministry.roles
      .filter((role) => quantityOf(event, role) > 0)
      .map((role): FunctionSlot => {
        const qty = quantityOf(event, role);
        const ids = event.slots[role.id] ?? [];
        needed += qty;
        filled += Math.min(ids.length, qty);

        const people = ids.flatMap((id, index): SlotPerson[] => {
          const member = lookup.members.get(id);

          if (!member) return [];

          let issue = memberIssue(member, role, event, past);

          if (!issue && seen.has(id)) {
            issue = {
              kind: 'duplicate',
              label: 'Já escalado neste evento',
              message: `${member.name} já está escalado(a) em outra função deste evento.`,
            };
          }

          seen.add(id);

          if (issue) conflicts.push({ member, role, ministry, issue });

          return [
            {
              member,
              availability: availabilityAt(member, event.date, event.time),
              issue,
              extra: index >= qty,
            },
          ];
        });

        if (people.length < qty) {
          pending.push({ role, ministry, have: people.length, missing: qty - people.length });
        }

        return { role, qty, people, open: Math.max(0, qty - people.length) };
      });

    return [{ ministry, functions }];
  });

  const status = statusOf(event, { conflicts: conflicts.length, filled, needed });

  return { event, groups, filled, needed, conflicts, pending, people: [...seen], status };
}

/**
 * Recorte do evento só com alguns ministérios (o Líder de Ministério vê apenas os que
 * lidera) e, opcionalmente, só com algumas funções (o Líder de Função, as suas).
 */
export function scopeInfo(
  info: EventInfo,
  ministryIds: readonly string[] | null,
  roleIds: readonly string[] | null = null
): EventInfo {
  if (!ministryIds) return info;

  const inRoles = (roleId: string) => !roleIds || roleIds.includes(roleId);
  const groups = info.groups
    .filter((g) => ministryIds.includes(g.ministry.id))
    .map((g) => (roleIds ? { ...g, functions: g.functions.filter((f) => inRoles(f.role.id)) } : g))
    .filter((g) => g.functions.length > 0);
  let filled = 0;
  let needed = 0;
  const people = new Set<string>();

  for (const group of groups) {
    for (const fn of group.functions) {
      needed += fn.qty;
      filled += Math.min(fn.people.length, fn.qty);
      fn.people.forEach((p) => people.add(p.member.id));
    }
  }

  const conflicts = info.conflicts.filter((c) => ministryIds.includes(c.ministry.id) && inRoles(c.role.id));
  const pending = info.pending.filter((p) => ministryIds.includes(p.ministry.id) && inRoles(p.role.id));

  return {
    ...info,
    groups,
    filled,
    needed,
    conflicts,
    pending,
    people: [...people],
    status: statusOf(info.event, { conflicts: conflicts.length, filled, needed }),
  };
}

/**
 * Quem serve no evento, de fato: os escalados, menos quem faltou no check-in, mais quem
 * serviu no lugar de alguém. Sem check-in, é a própria escala.
 */
export function participantsOf(event: ScheduleEvent): Set<string> {
  const ids = new Set(Object.values(event.slots).flat());

  for (const [memberId, mark] of Object.entries(event.attendance)) {
    if (mark.status !== 'absent' || !ids.has(memberId)) continue;

    ids.delete(memberId);
    if (mark.substituteId) ids.add(mark.substituteId);
  }

  return ids;
}

/**
 * Participações de cada membro nos eventos do período — o ano (`'2026'`) ou o mês
 * (`'2026-10'`) —, uma por evento, mesmo que esteja em duas funções dele. Vale o check-in:
 * falta não conta e servir no lugar de alguém conta (`participantsOf`).
 */
export function participationCounts(events: readonly ScheduleEvent[], period: string): Map<string, number> {
  const counts = new Map<string, number>();

  for (const event of events) {
    if (!event.date.startsWith(period)) continue;

    for (const id of participantsOf(event)) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }

  return counts;
}

export function monthlyCounts(events: readonly ScheduleEvent[], month: string): Map<string, number> {
  return participationCounts(events, month);
}

/** Função em que o membro está escalado no evento, se estiver. */
export function roleOfMember(event: ScheduleEvent, memberId: string, lookup: Lookup): Role | null {
  for (const [roleId, ids] of Object.entries(event.slots)) {
    if (ids.includes(memberId)) {
      return lookup.roles.get(roleId)?.role ?? null;
    }
  }

  return null;
}

export function isScheduledIn(event: ScheduleEvent, memberId: string): boolean {
  return Object.values(event.slots).some((ids) => ids.includes(memberId));
}

/**
 * O evento tem alguma posição a preencher. Sem ministério (ou com todas as funções fora),
 * é um evento só do calendário: não tem escala, não entra na geração nem nas contas da
 * escala, e o status (`awaiting`) não vale para ele.
 */
export function hasPositions(info: EventInfo): boolean {
  return info.needed > 0;
}

/**
 * Resumo do preenchimento: "8 escalados · 1 função pendente", "16 posições · aguardando
 * geração" ou "Sem escala" (evento sem posições).
 */
export function fillSummary(info: EventInfo): string {
  if (!hasPositions(info)) return 'Sem escala';
  if (!info.event.generated) return `${info.needed} posições · aguardando geração`;

  const missing = info.needed - info.filled;
  const pending = missing
    ? `${missing} ${missing === 1 ? 'função pendente' : 'funções pendentes'}`
    : 'nenhuma pendência';

  return `${info.people.length} escalados · ${pending}`;
}

/** Cabeçalho da função no painel: "Completa · +1 extra", "Falta 1". */
export function fillStateLabel(have: number, qty: number): string {
  if (have < qty) return `Falta ${qty - have}`;
  if (have === qty) return 'Completa';

  return `Completa · +${have - qty} extra`;
}

export type CandidateTag = 'available' | 'unset' | 'unavailable' | 'blackout' | 'scheduled' | 'sex';

export interface Candidate {
  member: Member;
  tag: CandidateTag;
  /** Regra que a escolha viola, quando viola — a interface pede confirmação. */
  issue: { title: string; detail: string } | null;
  /** Regras avançadas da função que a escolha não cumpre ("Par restrito", "Pouca experiência"…). */
  rules: RoleRuleIssue[];
  /** Em treinamento nesta função. */
  trainee: boolean;
  /** Escalas anteriores nesta função. */
  experience: number;
  /** O parceiro de "servem juntos" que já está nesta função do evento (`null` sem ele). */
  partnerId: string | null;
  monthCount: number;
  suggested: boolean;
}

const TAG_RANK: Record<CandidateTag, number> = {
  available: 0,
  unset: 1,
  scheduled: 2,
  sex: 3,
  unavailable: 4,
  blackout: 5,
};

/**
 * Quem pode ocupar uma vaga da função, do melhor para o pior: disponível › não
 * configurado › com impedimento; entre iguais, quem completa um par que serve junto vem
 * antes. O primeiro sem impedimento é o "Sugerido". Fora quem já
 * está na função, nunca exclui ninguém habilitado em silêncio — quem viola regra aparece
 * com o motivo.
 */
export function evaluateCandidates(
  event: ScheduleEvent,
  roleId: string,
  lookup: Lookup,
  options: { replaceId?: string | null; monthCounts: Map<string, number>; limit: number }
): Candidate[] {
  const entry = lookup.roles.get(roleId);

  if (!entry) return [];

  const { role, ministry } = entry;
  const inEvent = new Set(Object.values(event.slots).flat());
  const roles = eventRoles(event, lookup);
  // Quem a pessoa substitui sai da conta (do par restrito e do acompanhante do treinamento).
  const base = options.replaceId
    ? {
        ...event,
        slots: {
          ...event.slots,
          [roleId]: (event.slots[roleId] ?? []).filter((id) => id !== options.replaceId),
        },
      }
    : event;
  const nameOf = (id: string) => lookup.members.get(id)?.name ?? 'outra pessoa';

  const candidates = [...lookup.members.values()]
    .filter(
      (m) =>
        isActiveMember(m) &&
        isEnabledIn(m, ministry.id, role.id) &&
        m.id !== options.replaceId &&
        !(event.slots[roleId] ?? []).includes(m.id)
    )
    .map((member): Candidate => {
      const monthCount = options.monthCounts.get(member.id) ?? 0;
      const availability = availabilityAt(member, event.date, event.time);
      const blackout = blackoutOn(member, event.date);
      let tag: CandidateTag = availability === 'available' ? 'available' : 'unset';
      let issue: Candidate['issue'] = null;

      const declined = event.responses[member.id];

      if (declined?.status === 'declined' && !inEvent.has(member.id)) {
        tag = 'unavailable';
        issue = {
          title: 'Avisou que não pode ir',
          detail: `${member.name} avisou que não pode ir a este evento${declined.reason ? `: ${declined.reason}` : '.'}`,
        };
      } else if (inEvent.has(member.id)) {
        tag = 'scheduled';
        issue = { title: RULE_MESSAGES.duplicate, detail: `${member.name} já tem uma função neste evento.` };
      } else if (blackout) {
        tag = 'blackout';
        issue = {
          title: RULE_MESSAGES.blackout,
          detail: `${blackout.reason || 'Ausência programada'} de ${formatDayMonth(blackout.from)} a ${formatDayMonth(blackout.to)}.`,
        };
      } else if (availability === 'unavailable') {
        tag = 'unavailable';
        issue = {
          title: RULE_MESSAGES.unavailable,
          detail: `${member.name} marcou indisponibilidade em ${periodText(event.date, event.time)}.`,
        };
      } else if (!sexAllowed(role, member)) {
        tag = 'sex';
        issue = {
          title: RULE_MESSAGES.sex,
          detail: `${role.name} aceita apenas ${role.allowedSex === 'F' ? 'mulheres' : 'homens'}.`,
        };
      } else if (monthCount >= options.limit) {
        issue = {
          title: RULE_MESSAGES.limit,
          detail: `${member.name} já tem ${monthCount} escalas no mês (limite de ${options.limit}).`,
        };
      }

      const experience = lookup.experience.count(member.id, role.id, event.date);
      const rules = inEvent.has(member.id)
        ? []
        : roleRuleIssues(member, role, base, roles, experience, nameOf);

      if (!issue && rules[0]) issue = { title: rules[0].title, detail: rules[0].detail };

      return {
        member,
        tag,
        issue,
        rules,
        trainee: isTrainee(role, member.id),
        experience,
        partnerId: inEvent.has(member.id) ? null : togetherPartnerIn(base, role, member.id),
        monthCount,
        suggested: false,
      };
    })
    .sort(
      (a, b) =>
        TAG_RANK[a.tag] - TAG_RANK[b.tag] ||
        Number(!!a.issue) - Number(!!b.issue) ||
        Number(!a.partnerId) - Number(!b.partnerId) ||
        a.monthCount - b.monthCount ||
        compareNames(a.member.name, b.member.name)
    );

  const best = candidates.find((c) => c.tag === 'available' && !c.issue);

  if (best) best.suggested = true;

  return candidates;
}

/**
 * Substituto automático para quem está em conflito: habilitado, disponível, sem
 * ausência, fora do evento, dentro da restrição da função e o menos escalado no mês.
 */
export function suggestReplacement(
  event: ScheduleEvent,
  roleId: string,
  memberId: string,
  lookup: Lookup,
  monthCounts: Map<string, number>
): Member | null {
  const pick = evaluateCandidates(event, roleId, lookup, {
    replaceId: memberId,
    monthCounts,
    limit: Number.POSITIVE_INFINITY,
  }).find((c) => c.tag === 'available' && !c.issue);

  return pick?.member ?? null;
}

export interface MoveTarget {
  roleId: string;
  label: string;
  enabled: boolean;
  /** Por que não pode, ou o aviso de que entra como extra. */
  reason: string;
}

/** Para onde a pessoa pode ser movida dentro do evento, com o motivo quando não pode. */
export function moveTargets(
  event: ScheduleEvent,
  memberId: string,
  fromRoleId: string,
  lookup: Lookup
): MoveTarget[] {
  const member = lookup.members.get(memberId);

  if (!member) return [];

  return event.ministryIds.flatMap((ministryId) => {
    const ministry = lookup.ministries.get(ministryId);

    if (!ministry) return [];

    return ministry.roles
      .filter((role) => role.id !== fromRoleId && quantityOf(event, role) > 0)
      .map((role): MoveTarget => {
        const label = `${role.name} · ${ministry.name}`;

        if (!isEnabledIn(member, ministry.id, role.id)) {
          return { roleId: role.id, label, enabled: false, reason: 'Não habilitado(a)' };
        }

        if (!sexAllowed(role, member)) {
          return {
            roleId: role.id,
            label,
            enabled: false,
            reason: role.allowedSex === 'F' ? 'Restrita a mulheres' : 'Restrita a homens',
          };
        }

        const have = (event.slots[role.id] ?? []).length;

        return {
          roleId: role.id,
          label,
          enabled: true,
          reason: have >= quantityOf(event, role) ? 'Completa · entra como extra' : '',
        };
      });
  });
}
